import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Star as StarIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useValuePackForm } from 'hooks/useValuePackForm';
import { groupTiersByDurationCountry, calculatePriceRange, getDefaultTierFilter } from 'utils/valuePack/valuePackUtils';
import { getCountryByCode } from 'utils/valuePack/countryUtils';

const TiersStep = ({ stepIndex = 2, isEdit }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { 
    getFieldValue, 
    hasFieldError, 
    getFieldError,
    loading: formLoading 
  } = useValuePackForm(stepIndex, isEdit);
  
  const { 
    formData, 
    isEditMode, 
    loading: storeLoading 
  } = useSelector((state) => state.valuePack);

  const isLoading = formLoading || storeLoading;
  const tiers = getFieldValue('tiers') || formData.tiers || [];
  const tierGroups = useMemo(() => groupTiersByDurationCountry(tiers), [tiers]);
  const hasAnyTiers = tiers && tiers.length > 0;

  const [filterDuration, setFilterDuration] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const defaultFiltersApplied = useRef(false);

  useEffect(() => {
    if (defaultFiltersApplied.current) return;
    if (!tiers.length) return;

    defaultFiltersApplied.current = true;
    const defaults = getDefaultTierFilter(tiers);
    setFilterCountry(defaults.country);
    setFilterDuration(defaults.duration);
  }, [tiers]);

  const availableDurations = useMemo(() => {
    const durations = new Set();
    Object.values(tierGroups).forEach((group) => durations.add(group.duration));
    return Array.from(durations).sort((a, b) => a - b);
  }, [tierGroups]);

  const availableCountries = useMemo(() => {
    const countries = new Map();
    Object.values(tierGroups).forEach((group) => {
      if (group.country && !countries.has(group.country)) {
        countries.set(group.country, getCountryByCode(group.country));
      }
    });
    return Array.from(countries.entries());
  }, [tierGroups]);

  const filteredGroupKeys = useMemo(() => {
    return Object.keys(tierGroups).filter((key) => {
      const group = tierGroups[key];
      const matchesDuration =
        filterDuration === 'all' || String(group.duration) === String(filterDuration);
      const matchesCountry =
        filterCountry === 'all' || group.country === filterCountry;
      return matchesDuration && matchesCountry;
    });
  }, [tierGroups, filterDuration, filterCountry]);

  const renderDurationCountryFilter = useCallback(() => (
    <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Duration</InputLabel>
        <Select
          label="Duration"
          value={filterDuration}
          onChange={(e) => setFilterDuration(e.target.value)}
        >
          <MenuItem value="all">All Durations</MenuItem>
          {availableDurations.map((duration) => (
            <MenuItem key={duration} value={duration}>
              {duration} Month{duration > 1 ? 's' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Country</InputLabel>
        <Select
          label="Country"
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
        >
          <MenuItem value="all">All Countries</MenuItem>
          {availableCountries.map(([code, countryData]) => (
            <MenuItem key={code} value={code}>
              {countryData?.name || code} ({code})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  ), [filterDuration, filterCountry, availableDurations, availableCountries]);

  const handleManageTiers = useCallback(() => {
    if (isEditMode && id) {
      navigate(`/value-pack/${id}/tiers`);
    }
  }, [isEditMode, id, navigate]);

  const renderTierGroupCard = useCallback((groupKey, group) => {
    const priceRange = calculatePriceRange(group.tiers, group.country);
    const countryData = getCountryByCode(group.country);

    return (
      <Card key={groupKey} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {group.duration} Month{group.duration > 1 ? 's' : ''}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <PublicIcon fontSize="small" color="action" />
                <Chip 
                  label={countryData?.name || group.country} 
                  size="small" 
                  variant="outlined" 
                  color="primary"
                />
              </Box>
            </Box>
            <Box textAlign="right">
              <Typography variant="h6" color="primary">
                {countryData?.currency || '$'} {priceRange.min}
                {priceRange.min !== priceRange.max && ` - ${priceRange.max}`}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {group.tiers.length} tier{group.tiers.length > 1 ? 's' : ''} configured
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {group.tiers.map(tier => (
              <Chip
                key={tier.id}
                label={`${tier.tierType}${tier.isPopular ? ' ⭐' : ''}`}
                size="small"
                color={
                  tier.tierType === 'Low' ? 'info' :
                  tier.tierType === 'Mid' ? 'warning' : 'success'
                }
                variant={tier.isPopular ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Accordion sx={{ boxShadow : "none"}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">View Tier Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {group.tiers.map(tier => {
                  const pricing = tier?.currentPrice;
                  const hasDiscount = pricing?.discount && pricing.discount > 0;
                  const originalPrice = hasDiscount ? (pricing.price + pricing.discount) : pricing?.price;

                  return (
                    <Grid item xs={12} md={4} key={tier.id}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          border: '1px solid', 
                          borderColor: 'divider', 
                          borderRadius: 1,
                          bgcolor: tier.isPopular ? 'action.hover' : 'transparent'
                        }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          {tier.name || `${tier.tierType} Tier`}
                          {tier.isPopular && (
                            <StarIcon sx={{ ml: 1, fontSize: 16, color: 'secondary.main' }} />
                          )}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {tier.description || 'No description'}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {countryData?.currency || '$'} {pricing || 0}
                          {hasDiscount && (
                            <Typography component="span" variant="body2" sx={{ 
                              textDecoration: 'line-through', 
                              ml: 1, 
                              color: 'text.secondary' 
                            }}>
                              {countryData?.currency || '$'} {originalPrice}
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                          Features: {(tier.featuresConfiguration?.length || 0) + (tier.additionalFeatures?.length || 0)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  }, []);

  const renderTierStatistics = useCallback(() => {
    const tierGroupKeys = Object.keys(tierGroups);
    const popularTiersCount = tiers.filter(t => t.isPopular).length;
    const uniqueDurations = new Set(tiers.map(t => t.duration)).size;

    return (
      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>
          Summary Statistics
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="info.light" borderRadius={1}>
              <Typography variant="h4" color="info.dark">
                {tierGroupKeys.length}
              </Typography>
              <Typography variant="caption" color="info.dark">
                Duration-Country Combinations
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={1}>
              <Typography variant="h4" color="success.dark">
                {tiers.length}
              </Typography>
              <Typography variant="caption" color="success.dark">
                Total Tiers Configured
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={1}>
              <Typography variant="h4" color="warning.dark">
                {popularTiersCount}
              </Typography>
              <Typography variant="caption" color='warning.dark'>
                Popular Tiers
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="secondary.light" borderRadius={1}>
              <Typography variant="h4" color="secondary.dark">
                {uniqueDurations}
              </Typography>
              <Typography variant="caption" color="secondary.dark">
                Duration Options
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }, [tierGroups, tiers]);

  const renderTierManagement = useCallback(() => {
    const tierGroupKeys = Object.keys(tierGroups);

    return (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
          <Typography variant="subtitle1">
            Current Tier Configurations ({filteredGroupKeys.length} of {tierGroupKeys.length} combinations)
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleManageTiers}
            startIcon={<EditIcon />}
            size="small"
            sx={{ color: "#fff" }}
          >
            Manage All Tiers
          </Button>
        </Box>

        {renderDurationCountryFilter()}

        {filteredGroupKeys.length > 0 ? (
          filteredGroupKeys.map(groupKey =>
            renderTierGroupCard(groupKey, tierGroups[groupKey])
          )
        ) : (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              No tier configurations match the selected filters
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilterDuration('all');
                setFilterCountry('all');
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}

        {renderTierStatistics()}
      </>
    );
  }, [tierGroups, filteredGroupKeys, handleManageTiers, renderTierGroupCard, renderTierStatistics, renderDurationCountryFilter]);

  const renderEmptyState = useCallback(() => (
    <Box textAlign="center" py={4}>
      <InfoIcon color="action" sx={{ fontSize: 48, mb: 2 }} />
      <Typography variant="h6" color="textSecondary" gutterBottom>
        No Pricing Tiers Configured
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Set up different pricing tiers to offer various feature combinations and pricing options 
        across different markets and subscription durations
      </Typography>
    </Box>
  ), []);

  const renderTierInfo = useCallback(() => (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            What are Pricing Tiers?
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Create flexible pricing strategies with our tier system that supports multiple markets and subscription durations.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" p={2}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Low Tier
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Basic features at an entry-level price point. Perfect for small businesses or startups.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" p={2}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  <StarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Mid Tier
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Popular choice with balanced features and pricing. Ideal for growing businesses.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" p={2}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  High Tier
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Premium features with advanced capabilities. Best for established enterprises.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Multi-Market Support:</strong> Configure different pricing for various countries and subscription durations. 
              Each combination can have its own Low, Mid, and High tier with customized features and pricing.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Grid>
  ), []);

  const getCustomTierError = useCallback(() => {
    const fieldError = getFieldError('tiers');
    
    if (isEdit && !hasAnyTiers && fieldError) {
      return 'You cannot update value pack without creating tiers first. Please click "Manage Pricing Tiers" to add at least one pricing tier.';
    }
    
    return fieldError;
  }, [getFieldError, isEdit, hasAnyTiers]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography ml={2}>Loading tier configurations...</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pricing Tiers Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Configure different pricing tiers for this value pack with varying features and pricing across different markets and durations
            </Typography>

            {isEdit && !hasAnyTiers && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <AlertTitle>Action Required</AlertTitle>
                <Typography variant="body2">
                  <strong>You cannot update this value pack without creating pricing tiers.</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Please click "Add First Pricing Tier" to add at least one pricing tier before updating.
                </Typography>
              </Alert>
            )}
            
            {hasFieldError('tiers') && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Validation Error</AlertTitle>
                {getFieldError('tiers')}
              </Alert>
            )}
            
            {!isEditMode ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>Create First, Then Add Tiers</AlertTitle>
                You can configure pricing tiers after creating the value pack. 
                This allows you to set up different pricing levels with varying features and discounts for different markets and subscription durations.
              </Alert>
            ) : (
              <>
                {hasAnyTiers ? renderTierManagement() : renderEmptyState()}
                
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleManageTiers}
                    startIcon={<AddIcon />}
                    size="large"
                    sx={{ color: "#fff" }}
                  >
                    {hasAnyTiers ? 'Manage Pricing Tiers' : 'Add First Pricing Tier'}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {!isEditMode && renderTierInfo()}
    </Grid>
  );
};

export default React.memo(TiersStep);
